import classNames from 'classnames';
import { PropsWithChildren, useState } from 'react';
import { AddressDetails } from '../../../sharedComponents/AddressDetails';
import { LabelWithTimezone } from '../../../sharedComponents/LabelWithTimezone';
import { NoValue } from '../../../sharedComponents/NoValue';
import { StatusLabel } from '../../../sharedComponents/StatusLabel';
import { LabelledInfoCell } from '../../../sharedComponents/sidebar/LabelledInfoCell';
import { UnacknowledgedChangesAlert } from '../../../sharedComponents/sidebar/UnacknowledgedChangesAlert';
import { DateTime } from '../../../sharedComponents/table/BaseTableComponents';
import { ServiceOption } from '../../serviceOptions/serviceOption.types';
import { ServiceOrder } from '../../serviceOrders/serviceOrder.types';
import { hasServiceOptionId, ServiceOrderOrOptionLinkButton } from './ServiceOrderOrOptionLinkButton';
import { ServiceOrderOrOptionModelDetails } from './ServiceOrderOrOptionModelDetails';

type ServiceOrderOrOptionSidebarHeaderProps = {
    serviceOrderOrOption: ServiceOrder | ServiceOption;
};

const hasUnacknowledgedExternalChanges = (serviceOrderOrOption: ServiceOrder | ServiceOption) => {
    return 'hasUnacknowledgedExternalChanges' in serviceOrderOrOption
        ? serviceOrderOrOption.hasUnacknowledgedExternalChanges
        : undefined;
};

export const ServiceOrderOrOptionSidebarHeader = (props: ServiceOrderOrOptionSidebarHeaderProps) => {
    const { serviceOrderOrOption } = props;
    const [hasChanges, setHasChanges] = useState(hasUnacknowledgedExternalChanges(serviceOrderOrOption));

    const rowClasses = 'display-flex gap-10';
    const highlightedRowClasses = 'border-color-light border-style-solid border-width-1 border-bottom-only';

    return (
        <div>
            <div className={classNames('bg-lightest')}>
                {hasChanges && (
                    <UnacknowledgedChangesAlert
                        handleOnClick={setHasChanges}
                        alertMessage="outboundPortal.serviceOrders.alerts.unacknowledgedChanges"
                    />
                )}
                <div className={classNames(rowClasses, highlightedRowClasses, 'padding-20')}>
                    <LabelledInfoCell
                        labelMessageId="outboundPortal.serviceOrder.sidebar.serviceOrderId"
                        className="text-size-16 text-medium"
                    >
                        {serviceOrderOrOption.externalOrderId}
                        <ServiceOrderOrOptionLinkButton serviceOrderOrOption={serviceOrderOrOption} />
                    </LabelledInfoCell>
                    <LabelledInfoCell labelMessageId="outboundOrderBook.common.heading.status" className="text-size-16">
                        <StatusLabel status={serviceOrderOrOption.status} />
                    </LabelledInfoCell>
                </div>
            </div>
            <div className={classNames(rowClasses, 'padding-20')}>
                <LabelledInfoCell
                    labelMessageId="outboundPortal.serviceOrder.sidebar.vehicleInformation"
                    className="text-medium"
                >
                    {serviceOrderOrOption.vehicle.id.vin ?? serviceOrderOrOption.vehicle.id.productionNumber ?? (
                        <NoValue />
                    )}
                </LabelledInfoCell>
                <TimeZoneLabelledInfoCell
                    labelMessageId="outboundOrderBook.common.heading.orderIssuedAt"
                    timeZone={serviceOrderOrOption.serviceLocation.timeZone}
                    date={serviceOrderOrOption.orderIssuedAt}
                >
                    <DateTime
                        dateTime={serviceOrderOrOption.orderIssuedAt}
                        timeZone={serviceOrderOrOption.serviceLocation.timeZone}
                    />
                </TimeZoneLabelledInfoCell>
            </div>
            <div className={classNames(rowClasses, 'padding-x-20', 'padding-bottom-20')}>
                <LabelledInfoCell labelMessageId="outboundOrderBook.serviceOrder.table.heading.location">
                    <AddressDetails
                        address={serviceOrderOrOption.serviceLocation.address ?? {}}
                        identifier={serviceOrderOrOption.serviceLocation.identifier}
                    />
                </LabelledInfoCell>
                {hasServiceOptionId(serviceOrderOrOption) && (
                    <LabelledInfoCell labelMessageId="outboundPortal.serviceOrders.table.externalOptionId">
                        {serviceOrderOrOption.externalOptionId}
                    </LabelledInfoCell>
                )}
            </div>
            <ServiceOrderOrOptionModelDetails model={serviceOrderOrOption.vehicle.model} />
        </div>
    );
};

type TimeZoneLabelledInfoCellProps = PropsWithChildren<{
    labelMessageId: string;
    timeZone: string;
    date?: Date | number;
    className?: string;
}>;

const TimeZoneLabelledInfoCell = ({
    labelMessageId,
    timeZone,
    date,
    className,
    children,
}: TimeZoneLabelledInfoCellProps) => (
    <div className="flex-1-1 width-50pct">
        <LabelWithTimezone messageId={labelMessageId} timeZoneData={{ timeZone, date }} />
        <div className={classNames('word-break', className)}>{children}</div>
    </div>
);
